import React, { useState, useEffect } from 'react';
import { Footer, Spinner } from 'components/common/Modal';
import { gql, useMutation } from '@apollo/client';
import GraphQlImageModel from '@/components/interfaces/GraphQlImageModel';
import { ImageModelType } from './types';
import ImageModelCard from './ImageModelCard';
import styles from './ExistingImagePicker.module.scss';

export const COPY_IMAGE_MODEL = gql`
  mutation CopyExistingImageModel(
    $modelId: ID!,
    $modelType: String!,
    $existingImageModelId: ID!,
    $imageModelId: ID,
    $readOnly: Boolean
  ) {
    copyExistingImageModel(
      modelId: $modelId,
      modelType: $modelType,
      existingImageModelId: $existingImageModelId,
      imageModelId: $imageModelId,
      readOnly: $readOnly
    ) {
      imageModel {
        id,
        alignment,
        captionEn,
        captionEs,
        decorative,
        displaySize,
        displayStyle,
        hideCaptions,
        snippet,
        zoomType,
        image {
          id,
          credits,
          descriptionConfidence,
          descriptionEn,
          descriptionEs,
          imageUrl,
          fileName,
          resolution,
          thumbnail,
          titleEn,
          titleEs
        }
      }
    }
  }
`;

interface ExistingImagePickerPropTypes {
  afterSubmit: (result: GraphQlImageModel | ImageModelType) => void;
  closeModal: () => void;
  fromButton?: boolean;
  getExistingImages: () => Promise<ImageModelType[]>;
  imageModelId?: number;
  modelId: string | number;
  modelType: string;
  reuseInPlace?: boolean;
}

const ExistingImagePicker = ({
  afterSubmit,
  closeModal,
  fromButton,
  getExistingImages,
  imageModelId,
  modelId,
  modelType,
  reuseInPlace = false,
}: ExistingImagePickerPropTypes) => {
  const [copyExistingImageModelMutation, { loading: submitting }] = useMutation(COPY_IMAGE_MODEL);
  const [imageModels, setImageModels] = useState<ImageModelType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [selectedImageModelId, setSelectedImageModelId] = useState(0);

  useEffect(() => {
    getExistingImages()
      .then((data: ImageModelType[]) => setImageModels(data))
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
        setError(e);
      })
      .finally(() => setLoading(false));
  }, []);

  const submit = () => {
    // AIDEV-NOTE: PLAT-17696 — reuseInPlace inserts the picked image model's own snippet instead of
    // copying it, so a reference material never grows a second Image-table row for the same image and
    // an Image-table edit still resolves everywhere the snippet appears.
    if (reuseInPlace) {
      const picked = imageModels.find(({ id }) => id.toString() === selectedImageModelId.toString());

      // Unreachable while the Submit button stays disabled without a selection, and strictNullChecks
      // is off so the compiler won't catch it if that ever changes.
      if (!picked) return;

      afterSubmit(picked);
      closeModal();
      return;
    }

    const variables = {
      modelId,
      modelType,
      imageModelId,
      existingImageModelId: selectedImageModelId,
      readOnly: fromButton,
    };

    copyExistingImageModelMutation({ variables })
      .then((result) => {
        const { imageModel } = result.data.copyExistingImageModel;

        afterSubmit(imageModel);
        closeModal();
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(error);
        setError(e);
      });
  };

  if (loading) return <Spinner isLoading />;
  if (error) return <p>Something went wrong. Please refresh and try again.</p>;

  return (
    <section aria-label="image-picker-container">
      <div className={styles.existingImagesContainer}>
        {imageModels.length === 0 && <p>No images found.</p>}

        {imageModels.map(imageModel => (
          <ImageModelCard
            key={imageModel.id}
            onSelect={() => setSelectedImageModelId(imageModel.id)}
            imageModel={imageModel}
            selected={imageModel.id.toString() === selectedImageModelId.toString()}
          />
        ))}
      </div>

      <Footer
        submitting={submitting}
        secondaryButtonCallback={closeModal}
        primaryButtonSubmit={false}
        primaryButtonText="Submit"
        primaryButtonDisabled={!selectedImageModelId}
        primaryButtonCallback={submit}
      />
    </section>
  );
};

export default ExistingImagePicker;
